import React, { useState, useRef, useId } from 'react';
import { UploadCloud, X, FileText, CheckCircle2 } from 'lucide-react';
interface FileUploadProps {
  label: string;
  description?: string;
  accept?: string;
  /** Max file size in megabytes (default 10). */
  maxMB?: number;
  onChange: (file: File | null) => void;
  error?: string;
  value?: File | null;
  required?: boolean;
  id?: string;
}
/**
 * Standardized Maybank file upload zone.
 * Empty state: dashed border, cloud icon, "Click to upload or drag and drop" + format/size helper.
 * Filled state: solid border, file icon, name, green check, remove button.
 * WCAG 2.2 AA: keyboard activated (button semantic), visible focus ring, descriptive labels.
 */
export function FileUpload({
  label,
  description,
  accept = '.pdf,.jpg,.jpeg,.png',
  maxMB = 10,
  onChange,
  error,
  value,
  required,
  id
}: FileUploadProps) {
  const reactId = useId();
  const fieldId = id || `upload-${reactId}`;
  const helperId = `${fieldId}-help`;
  const errorId = `${fieldId}-err`;
  const describedBy = [error ? errorId : null, helperId].filter(Boolean).join(' ');
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [sizeError, setSizeError] = useState<string | null>(null);
  const validateAndPass = (file: File | null) => {
    if (!file) {
      onChange(null);
      setSizeError(null);
      return;
    }
    if (file.size > maxMB * 1024 * 1024) {
      setSizeError(`File is too large. Max size is ${maxMB} MB.`);
      return;
    }
    setSizeError(null);
    onChange(file);
  };
  const openPicker = () => inputRef.current?.click();
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files?.[0]) validateAndPass(e.dataTransfer.files[0]);
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) validateAndPass(e.target.files[0]);
  };
  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    setSizeError(null);
    if (inputRef.current) inputRef.current.value = '';
  };
  const displayedError = error || sizeError;
  return <div className="w-full" data-id="element-962">
      <div className="mb-1.5" data-id="element-963">
        <label htmlFor={fieldId} className="block text-sm font-medium text-gray-900" data-id="element-964">
          {label}
          {required && <span className="text-red-600 ml-0.5" aria-hidden="true" data-id="element-965">
              *
            </span>}
          {required && <span className="sr-only" data-id="element-966"> (required)</span>}
        </label>
        {description && <p className="text-xs text-gray-600 mt-0.5" data-id="element-967">{description}</p>}
      </div>

      {!value ? <button type="button" id={fieldId} onClick={openPicker} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} aria-describedby={describedBy} aria-invalid={displayedError ? true : undefined} className={`
            group relative w-full block border-2 border-dashed rounded-lg px-6 py-8 text-center transition-colors
            min-h-[160px]
            focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B8860B] focus-visible:ring-offset-2
            ${isDragging ? 'border-[#FFCC00] bg-[#FFCC00]/10' : displayedError ? 'border-red-500 bg-red-50' : 'border-gray-400 bg-gray-50 hover:border-gray-700 hover:bg-gray-100'}
          `} data-id="element-968">
          <input ref={inputRef} type="file" className="sr-only" accept={accept} onChange={handleFileChange} tabIndex={-1} aria-hidden="true" data-id="element-969" />
          <div className="flex flex-col items-center justify-center gap-3" data-id="element-970">
            <div className="p-3 bg-white border border-gray-200 rounded-full shadow-sm" data-id="element-971">
              <UploadCloud className="h-7 w-7 text-gray-700" aria-hidden="true" data-id="element-972" />
            </div>
            <div className="text-sm text-gray-700" data-id="element-973">
              <span className="font-semibold text-gray-900 underline underline-offset-2" data-id="element-974">
                Click to upload
              </span>{' '}
              or drag and drop
            </div>
          </div>
        </button> : <div className="relative flex items-center gap-3 p-3 border-2 border-gray-700 rounded-lg bg-white" data-id="element-975">
          <div className="flex-shrink-0 h-10 w-10 bg-gray-100 rounded-lg flex items-center justify-center text-gray-700" data-id="element-976">
            <FileText className="h-5 w-5" aria-hidden="true" data-id="element-977" />
          </div>
          <div className="flex-1 min-w-0" data-id="element-978">
            <p className="text-sm font-medium text-gray-900 truncate" data-id="element-979">
              {value.name}
            </p>
            <p className="text-xs text-gray-600" data-id="element-980">
              {(value.size / 1024 / 1024).toFixed(2)} MB
            </p>
          </div>
          <div className="flex-shrink-0 flex items-center gap-1" data-id="element-981">
            <CheckCircle2 className="h-5 w-5 text-green-600" aria-label="Uploaded" data-id="element-982" />
            <button type="button" onClick={handleRemove} aria-label={`Remove ${value.name}`} className="inline-flex items-center justify-center w-9 h-9 rounded text-gray-600 hover:text-red-600 hover:bg-red-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-600 transition-colors" data-id="element-983">
              <X className="h-5 w-5" aria-hidden="true" data-id="element-984" />
            </button>
          </div>
        </div>}

      <p id={helperId} className="mt-1.5 text-xs text-gray-600" data-id="element-985">
        Supported formats: PDF, JPG, PNG. Max file size: {maxMB}MB.
      </p>
      {displayedError && <p id={errorId} className="mt-1 text-sm text-red-700 font-medium" role="alert" data-id="element-986">
          {displayedError}
        </p>}
    </div>;
}